import TabPanel from '@/components/ui/tab/TabPanel'
import Tabs from '@/components/ui/tabs/Tabs'
import PC300Mesures from './PC300Mesures'
import DiagnosticMedicalVideoContainer from './DiagnosticMedicalVideo'
import DiagnosticStethoscopeContainer from './DiagnosticStethoscope'
import BiologicalsContainer from './Biologicals'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { DiagnosticRTCProvider } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAttendeeInfo, selectMeetingInfo, selectTwilioStunServer } from '@/redux/reducers/consultation.reducer'
import { selectPeerId, selectRoomByConsultationId } from '@/redux/reducers/room.reducer'
import { getRoomCreatorId } from '@/core/models/room.model'
import { selectCustomDevices } from '@/redux/reducers/custom-device.reducer'
import { Box, Container } from '@mui/system'
import { Alert, Grid } from '@mui/material'
import { SchillerECG } from './SchillerECG'
import { useMemo } from 'react'
import { EdanECG } from './EdanECG'
import { TouchECG } from './TouchECG'
import { MIRSpirometry } from './MIRSpirometry'
import { getPatientBiometrics } from '@/utils/medconnectDevices/patientBiometrics'
import { selectConsultationById } from '@/redux/reducers/consultation.reducer'

type AllDiagnosticsProps = {
  consultationId?: string
  /** For group consultations: the consultation ID linked to the shared room */
  roomConsultationId?: string
  isStreamer: boolean
  /** If set, switch to this tab by id (e.g. 'stethoscope', 'medicalVideo', 'biologicals', 'mesures') */
  activeTabId?: string | null
  /** Called when the user manually switches tabs, so the parent can clear the controlled activeTabId */
  onTabChange?: (tabId: string) => void
}

const TAB_IDS = ['mesures', 'medicalVideo', 'stethoscope', 'biologicals'] as const

export function AllDiagnostics({ consultationId, roomConsultationId, isStreamer, activeTabId, onTabChange }: Readonly<AllDiagnosticsProps>) {
  const activeTabIndex = activeTabId ? TAB_IDS.indexOf(activeTabId as any) : undefined
  const controlledTab = activeTabIndex !== undefined && activeTabIndex >= 0 ? activeTabIndex : undefined
  const effectiveRoomConsultationId = roomConsultationId || consultationId
  const { t } = useTranslation()
  const medicalVideoAttendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'medicalVideo'))
  const medicalVideoMeetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'medicalVideo'))
  const peerId = useAppSelector(selectPeerId)
  const room = useAppSelector((state) => (effectiveRoomConsultationId ? selectRoomByConsultationId(state, effectiveRoomConsultationId) : undefined))
  const stethoscopeAttendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'stethoscope'))
  const stethoscopeMeetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'stethoscope'))
  const twilioStunServer = useAppSelector(selectTwilioStunServer)
  const customDevices = useAppSelector(selectCustomDevices)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const biometrics = useMemo(() => getPatientBiometrics(consultation), [consultation])
  const peerConfig = useMemo(
    () =>
      !twilioStunServer
        ? undefined
        : {
          iceServers: twilioStunServer.iceServers,
        },
    [twilioStunServer]
  )

  if (!room) {
    return null
  }

  const peerjsConfig = {
    config: peerConfig,
    peerId,
    streamer: room.streamer,
  }

  return (
    <Container maxWidth="md" sx={{ pt: '20px', pb: '20px' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('component.ui.deviceCard.biometrics.info', {
          height: biometrics.height,
          weight: biometrics.weight,
          heightSource: t(`component.ui.deviceCard.biometrics.${biometrics.heightSource}`),
          weightSource: t(`component.ui.deviceCard.biometrics.${biometrics.weightSource}`),
        })}
        {(biometrics.heightSource === 'default' || biometrics.weightSource === 'default') && (
          <> {t('component.ui.deviceCard.biometrics.hint')}</>
        )}
      </Alert>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <SchillerECG consultationId={consultationId} />
        <EdanECG consultationId={consultationId} />
        <TouchECG consultationId={consultationId} />
        <MIRSpirometry consultationId={consultationId} />
      </Grid>
      <Tabs
        value={controlledTab}
        onChange={(newIndex) => { onTabChange?.(TAB_IDS[newIndex]) }}
        renderTabPanel={({ value }) => (
          <>
            <TabPanel value={value} index={0}>
              <PC300Mesures consultationId={consultationId} />
            </TabPanel>
            <TabPanel value={value} index={1} unmountOnExit>
              <Box sx={{ height: '100%', display: 'flex', '& > div': { flex: 1 } }}>
                <DiagnosticRTCProvider
                  config={{
                    chime: {
                      attendeeInfo: medicalVideoAttendeeInfo,
                      meetingInfo: medicalVideoMeetingInfo,
                    },
                    // peerjs: peerjsConfig,
                  }}
                  customDevices={customDevices}
                  isStreamer={isStreamer}
                  type="video"
                >
                  <DiagnosticMedicalVideoContainer videoHeight="75vh" videoWidth="100vw" consultationId={consultationId} roomConsultationId={effectiveRoomConsultationId} />
                </DiagnosticRTCProvider>
              </Box>
            </TabPanel>
            <TabPanel value={value} index={2} unmountOnExit>
              <DiagnosticRTCProvider
                config={{
                  chime: {
                    attendeeInfo: stethoscopeAttendeeInfo,
                    meetingInfo: stethoscopeMeetingInfo,
                    ecgSourceUserId: getRoomCreatorId(room),
                  },
                  //peerjs: peerjsConfig,
                }}
                customDevices={customDevices}
                isStreamer={isStreamer}
                type="audio"
              >
                <DiagnosticStethoscopeContainer consultationId={consultationId} roomConsultationId={effectiveRoomConsultationId} />
              </DiagnosticRTCProvider>
            </TabPanel>
            <TabPanel value={value} index={3}>
              <BiologicalsContainer consultationId={consultationId} />
            </TabPanel>
          </>
        )}
        tabs={_.compact([
          { label: t('page.localConsultation.sidebar.mesures.label'), sx: { fontWeight: 700 }, id: 'mesures' },
          { label: t('page.liveConsultation.sidebar.medicalVideo.label'), sx: { fontWeight: 700 }, id: 'medicalVideo' },
          { label: t('page.localConsultation.sidebar.stethoscope.label'), sx: { fontWeight: 700 }, id: 'stethoscope' },
          { label: t('page.localConsultation.sidebar.biologicals.label'), sx: { fontWeight: 700 }, id: 'biologicals' },
        ])}
        variant="fullWidth"
      />
    </Container>
  )
}
